import { Canvas, useThree } from "@react-three/fiber";
import { Environment, MeshReflectorMaterial, OrbitControls } from "@react-three/drei";
import type { OrbitControls as OrbitControlsImpl } from 'three-stdlib'

import * as THREE from "three";
import { useRef, useEffect, useState } from "react";
import { EdgeInteraction, EdgeInteractionProps } from "./UVMapper/Components/EdgeInteraction";
import React from "react";
import {
  EffectComposer,
  DepthOfField
} from "@react-three/postprocessing";
import { SidePanel } from "./UVMapper/Components/SidePanel";
import { GoalsOverlay } from "./UVMapper/Components/GoalsOverlay";
import { MobileToolbar } from "./UVMapper/Components/MobileToolbar";
import { MobileSideButtons } from "./UVMapper/Components/MobileSideButtons";
import { AppSettings } from "./AppSettings";
import cameraIcon from "./camera-black.svg";
import reset from "./reset.svg";

// Check if WebGL is available
function isWebGLAvailable(): boolean {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || canvas.getContext('webgl');
    return gl !== null;
  } catch {
    return false;
  }
}

// Detect low-end devices to skip heavy GPU effects
function isLowEndDevice(): boolean {
  // Check hardware concurrency (CPU cores)
  const lowCores = navigator.hardwareConcurrency <= 4;

  // Check device memory if available (Chrome only)
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const lowMemory = deviceMemory !== undefined && deviceMemory <= 4;

  // Check if mobile device
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  // Check for integrated graphics hints (rough heuristic)
  const canvas = document.createElement('canvas');
  const gl = canvas.getContext('webgl');
  let isIntegratedGPU = false;
  if (gl) {
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    if (debugInfo) {
      const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL).toLowerCase();
      isIntegratedGPU = renderer.includes('intel') || renderer.includes('integrated');
    }
  }

  return lowCores || lowMemory || isMobile || isIntegratedGPU;
}

// Cache the result to avoid repeated checks
const isLowEnd = isLowEndDevice();

// Wrapper to delay EffectComposer until GL context is ready
// Skipped entirely on low-end devices to prevent WebGL context loss
function PostProcessing() {
  const { gl } = useThree();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (gl && !isLowEnd) {
      // Small delay to ensure context is fully initialized
      const id = requestAnimationFrame(() => setReady(true));
      return () => cancelAnimationFrame(id);
    }
  }, [gl]);

  // Skip post-processing on low-end devices
  if (isLowEnd || !ready) return null;

  return (
    <EffectComposer>
      <DepthOfField focusDistance={0} focalLength={0.05} bokehScale={2} height={480} />
    </EffectComposer>
  );
}

type ActiveTool = 'cut' | 'sew' | 'unfold' | null;



export function UVView(props:{geo : THREE.BufferGeometry, goals:{stretchGoal:number|null, seamsGoal:number|null}, callFunction:() => void}
){
  let objectGeometry = props.geo;
  const canvas2DObject = useRef<HTMLCanvasElement | null>(null);
  if (!canvas2DObject.current) {
    canvas2DObject.current = document.createElement("canvas");
    canvas2DObject.current.setAttribute("id", "canvas2D");
  }
  const controls = useRef<OrbitControlsImpl>(null);
  const canvasDiv = useRef<HTMLDivElement>(null);
  const edgeInteract = useRef<EdgeInteractionProps>(null);
  const sidePanel = useRef<SidePanel>(null);
  const goalsOverlay = useRef<GoalsOverlay>(null);
  const goals = props.goals
  const practiceFunction = props.callFunction;
  const resetEdgesButton = useRef<HTMLButtonElement>(null);

  // Mobile state
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>(
    window.innerHeight > window.innerWidth ? 'portrait' : 'landscape'
  );
  const [mobileGoals, setMobileGoals] = useState({
    edgeSeams: 0,
    globalStretchness: 0
  });
  const [activeTool, setActiveTool] = useState<ActiveTool>(null);
  const [webglError, setWebglError] = useState<string | null>(null);

  // Mobile button refs
  const mobileToolCutButton = useRef<HTMLButtonElement>(null);
  const mobileToolSewButton = useRef<HTMLButtonElement>(null);
  const mobileToolUnfoldButton = useRef<HTMLButtonElement>(null);
  const mobileSideCutButton = useRef<HTMLButtonElement>(null);
  const mobileSideSewButton = useRef<HTMLButtonElement>(null);
  const mobileSideUnfoldButton = useRef<HTMLButtonElement>(null);

  // Check WebGL availability on mount
  useEffect(() => {
    if (!isWebGLAvailable()) {
      setWebglError('WebGL is not available. Please try refreshing the page or closing other browser tabs.');
    }
  }, []);

  // Handle resize and orientation changes
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      setIsMobile(width <= 768 || (width <= 896 && height < width));
      setOrientation(height > width ? 'portrait' : 'landscape');
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Use ResizeObserver to trigger canvas redraw when container size is ready
  useEffect(() => {
    if (!canvasDiv.current) return;

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const { width, height } = entry.contentRect;
        if (width > 0 && height > 0 && canvas2DObject.current) {
          // Just trigger resize - let EdgeInteraction handle canvas dimensions
          // Setting dimensions here clears the canvas and causes race conditions
          window.dispatchEvent(new Event('resize'));
        }
      }
    });

    observer.observe(canvasDiv.current);

    // Also trigger after delays as fallback
    const t1 = setTimeout(() => window.dispatchEvent(new Event('resize')), 100);
    const t2 = setTimeout(() => window.dispatchEvent(new Event('resize')), 500);
    const t3 = setTimeout(() => window.dispatchEvent(new Event('resize')), 1000);

    return () => {
      observer.disconnect();
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [props.geo]);

  // Sync click handlers from desktop buttons to mobile buttons
  useEffect(() => {
    const syncClickHandler = (
      sourceRef: React.RefObject<HTMLButtonElement>,
      targetRefs: React.RefObject<HTMLButtonElement>[]
    ) => {
      const source = sourceRef.current;
      if (!source) return;

      const handler = () => source.click();
      targetRefs.forEach(targetRef => {
        const target = targetRef.current;
        if (target) {
          target.onclick = handler;
        }
      });
    };

    // Small delay to ensure desktop buttons have their handlers set up
    const timeoutId = setTimeout(() => {
      if (sidePanel.current) {
        syncClickHandler(sidePanel.current.toolCutButton, [mobileToolCutButton, mobileSideCutButton]);
        syncClickHandler(sidePanel.current.toolSewButton, [mobileToolSewButton, mobileSideSewButton]);
        syncClickHandler(sidePanel.current.toolUnfoldButton, [mobileToolUnfoldButton, mobileSideUnfoldButton]);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Poll goals overlay for state changes (to update mobile toolbar)
  useEffect(() => {
    const interval = setInterval(() => {
      if (goalsOverlay.current) {
        const state = goalsOverlay.current.state;
        setMobileGoals({
          edgeSeams: state.edgeSeams,
          globalStretchness: state.globalStretchness
        });
      }
      if (sidePanel.current) {
        setActiveTool(sidePanel.current.state.activeTool);
      }
    }, 100);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (canvas2DObject.current) {
      canvasDiv.current?.appendChild(canvas2DObject.current);
    }

    const onKeyUp = (event: KeyboardEvent) => {
      if (event.code === "KeyF")
      {
        resetView();
      }

    };

    document.addEventListener("keyup", onKeyUp);
    return () => {
      document.removeEventListener("keyup", onKeyUp);
    };
  }, []);
  
  function resetView()
  {

    controls.current?.reset?.();
  }




  useEffect(() => {
    if(AppSettings.DEBUG)console.log("Props changed")
  }, [props.geo]);

  const isPortrait = orientation === 'portrait';

  // Buttons config for portrait side buttons (all on right)
  const sideButtons = [
    { ref: mobileSideCutButton, label: 'cut', tool: 'cut' as const },
    { ref: mobileSideSewButton, label: 'sew', tool: 'sew' as const },
    { ref: mobileSideUnfoldButton, label: 'unfold', tool: 'unfold' as const }
  ];

  if (webglError) {
    return (
      <div id="uvMapper" className="webgl-error">
        <div className="webgl-error-card">
          <div className="webgl-error-icon">⚠️</div>
          <h2>WebGL Error</h2>
          <p>{webglError}</p>
          <button className="webgl-error-btn" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      </div>
    );
  }

  return (
    <div id="uvMapper" >
      {/* Mobile Toolbar - tools and Next only in landscape */}
      {isMobile && (
        <MobileToolbar
          edgeSeams={mobileGoals.edgeSeams}
          seamsGoal={goals.seamsGoal}
          globalStretchness={mobileGoals.globalStretchness}
          stretchGoal={goals.stretchGoal}
          onNext={practiceFunction}
          showTools={!isPortrait}
          showNextButton={!isPortrait}
          toolCutButton={mobileToolCutButton}
          toolSewButton={mobileToolSewButton}
          toolUnfoldButton={mobileToolUnfoldButton}
          activeTool={activeTool}
        />
      )}

      <div id="canvas-container">
        <button
          className="insideCanvas"
          onClick={() => resetView()}
          aria-label="Reset camera view"
        >
          <img src={cameraIcon} alt="" />
        </button>
        <button
          className="insideCanvas offset"
          ref={resetEdgesButton}
          aria-label="Reset all edges"
        >
          <img src={reset} alt="" />
        </button>
        <GoalsOverlay ref={goalsOverlay} />
        <Canvas
          camera={{
            position: [0, 3, 3],
            fov: 60,
            near: 0.01,
            far: 1000,
          }}
          onCreated={({ gl }) => {
            const canvas = gl.domElement;
            canvas.addEventListener('webglcontextlost', (e) => {
              e.preventDefault();
              setWebglError('WebGL context was lost. Please refresh the page.');
            });
            canvas.addEventListener('webglcontextrestored', () => {
              setWebglError(null);
            });
          }}
        >
          <PostProcessing />
          {/*
// @ts-ignore */}
          <OrbitControls ref={controls} enablePan={false} />
          <color attach="background" args={["#A7C7E7"]} />
          <EdgeInteraction
            geometry={objectGeometry}
            canvas2DObject={canvas2DObject.current!}
            sidePanel={sidePanel}
            goalsOverlay={goalsOverlay}
            ref={edgeInteract}
            goals={goals}
            practiceFunction={practiceFunction}
            resetButton={resetEdgesButton}
          />
          <mesh rotation={[-Math.PI / 2, 0, 0]} position ={[0,-3,0]}>
            <planeGeometry args={[1000,1000]}/>
            {isLowEnd ? (
              // Simple material for low-end devices - no framebuffer overhead
              <meshStandardMaterial color="#8ab4d9" metalness={0.1} roughness={0.8} />
            ) : (
              <MeshReflectorMaterial
                blur={[300, 100]}
                resolution={512}
                mixBlur={1}
                roughness={0.2}
                mirror={1}/>
            )}
          </mesh>
          <Environment preset="city" />
        </Canvas>
      </div>
      <div id="side" >
        <div id="canvas" ref={canvasDiv}></div>

        {/* Portrait side buttons - right side only */}
        {isMobile && isPortrait && (
          <MobileSideButtons
            position="right"
            buttons={sideButtons}
            activeTool={activeTool}
            onNext={practiceFunction}
            goalsMet={
              (goals.seamsGoal === null || mobileGoals.edgeSeams <= goals.seamsGoal) &&
              (goals.stretchGoal === null || mobileGoals.globalStretchness <= goals.stretchGoal)
            }
            edgeSeams={mobileGoals.edgeSeams}
            seamsGoal={goals.seamsGoal}
            globalStretchness={mobileGoals.globalStretchness}
            stretchGoal={goals.stretchGoal}
          />
        )}

        {/* Desktop side panel - hidden on mobile via CSS */}
        <SidePanel ref={sidePanel} />
      </div>
    </div>
  );
}
